import React, { useState, useEffect, useRef } from 'react';
import useSWR from 'swr';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import ActionButton from './ui/action-button';
import ServiceToggle from './ServiceToggle';
import { Label } from './ui/label';
import { Camera, Search, Check, X, QrCode, Loader2, Users, Calendar, Clock, Clipboard, Mail, CheckCircle, AlertCircle, ChevronDown, ExternalLink, DollarSign, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import apiV2 from '../utils/apiV2';
import api from '../utils/api'; // Keep legacy for public endpoints
import TabPageLayout, { PageLoader, EmptyState } from './TabPageLayout';
import FormSection from './FormSection';
import jsQR from 'jsqr';
import { QRCodeCanvas } from 'qrcode.react';

const CheckInTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [checkInEnabled, setCheckInEnabled] = useState(false);
  const [checkInPin, setCheckInPin] = useState('000000');
  
  // Track original values to detect unsaved changes
  const [originalEnabled, setOriginalEnabled] = useState(false);
  const [originalPin, setOriginalPin] = useState('000000');
  
  // Computed: do we have unsaved changes?
  const hasUnsavedChanges = checkInEnabled !== originalEnabled || checkInPin !== originalPin;
  
  const [scanMode, setScanMode] = useState('manual');
  const [searchQuery, setSearchQuery] = useState('');
  const [checkingIn, setCheckingIn] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResultsModal, setShowSearchResultsModal] = useState(false);
  const [searchResultsPage, setSearchResultsPage] = useState(1);
  const searchResultsPerPage = 5;
  
  // QR Code modal for scanner link
  const [showScannerQR, setShowScannerQR] = useState(false);
  const [scannerQRUrl, setScannerQRUrl] = useState('');
  
  // QR Scanner dropdown
  const [showScannerDropdown, setShowScannerDropdown] = useState(false);
  const scannerDropdownRef = useRef(null);
  
  // SWR fetcher function using apiV2
  const fetcher = (url) => apiV2.get(url).then(res => {
    // Handle both array and object responses
    const data = res.data;
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.bookings)) return data.bookings;
    if (data && Array.isArray(data.items)) return data.items;
    return [];
  });
  
  // Use SWR for recent check-ins with auto-refresh
  const orgId = typeof window !== 'undefined' ? localStorage.getItem('organizationId') : null;
  const { data: recentCheckIns = [], error: checkInsError, isLoading: loadingCheckIns, mutate: refreshCheckIns } = useSWR(
    checkInEnabled && orgId ? `/organizations/${orgId}/bookings?checkedIn=true` : null,
    fetcher,
    {
      refreshInterval: 30000,
      revalidateOnFocus: true,
      dedupingInterval: 5000
    }
  );
  
  // Email modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailBooking, setEmailBooking] = useState(null);
  const [emailTo, setEmailTo] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Sorting and filtering
  const [sortBy, setSortBy] = useState('recent');
  const [filterDate, setFilterDate] = useState('all');
  const [checkInPage, setCheckInPage] = useState(1);
  const checkInsPerPage = 10;
  
  // Selected check-in for details
  const [selectedCheckIn, setSelectedCheckIn] = useState(null);
  const [showCheckInDetails, setShowCheckInDetails] = useState(false);
  
  // Check-in confirmation
  const [showCheckInConfirm, setShowCheckInConfirm] = useState(false);
  const [bookingToCheckIn, setBookingToCheckIn] = useState(null);
  
  // Camera refs
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [scanning, setScanning] = useState(false);
  const scanIntervalRef = useRef(null);

  useEffect(() => {
    fetchSettings();
    return () => {
      stopCamera();
    };
  }, []);
  
  // Close scanner dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (scannerDropdownRef.current && !scannerDropdownRef.current.contains(event.target)) {
        setShowScannerDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const fetchSettings = async () => {
    try {
      const orgResponse = await apiV2.get('/auth/organizations/me');
      const orgData = orgResponse.data;
      
      const enabled = orgData.checkInEnabled || false;
      const pin = orgData.checkInPin || '000000';
      
      setCheckInEnabled(enabled);
      setCheckInPin(pin);
      
      // Store original values to track changes
      setOriginalEnabled(enabled);
      setOriginalPin(pin);
    } catch (error) {
      console.error('Error fetching check-in settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSave = async () => {
    if (checkInEnabled && (!/^\d{6}$/.test(checkInPin))) {
      toast.error('PIN must be exactly 6 digits');
      return;
    }

    setSaving(true);
    try {
      await apiV2.patch('/auth/organizations/me', {
        checkInEnabled: checkInEnabled,
        checkInPin: checkInPin
      });

      toast.success('Check-in settings saved!');
      fetchSettings();
    } catch (error) {
      console.error('Error saving settings:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to save settings';
      toast.error(`Save error: ${errorMsg}`);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        setCameraActive(true);
        setScanning(true);
        startScanning();
      }
    } catch (error) {
      console.error('Camera error:', error);
      toast.error('Failed to access camera. Please check permissions.');
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
    setScanning(false);
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
    }
  };

  const startScanning = () => {
    scanIntervalRef.current = setInterval(() => {
      if (videoRef.current && canvasRef.current && videoRef.current.readyState === videoRef.current.HAVE_ENOUGH_DATA) {
        const canvas = canvasRef.current;
        const video = videoRef.current;
        
        canvas.height = video.videoHeight;
        canvas.width = video.videoWidth;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height);
        
        if (code) {
          handleQRCodeDetected(code.data);
        }
      }
    }, 300);
  };

  const handleQRCodeDetected = (qrData) => {
    try {
      const url = new URL(qrData);
      const pathParts = url.pathname.split('/');
      const confirmationCode = pathParts[pathParts.length - 1];
      
      if (confirmationCode && confirmationCode.startsWith('CS-')) {
        stopCamera();
        setSearchQuery(confirmationCode);
        toast.success(`QR Code scanned: ${confirmationCode}`);
        setTimeout(() => handleManualCheckIn(), 500);
      }
    } catch (error) {
      // Invalid QR code format
    }
  };

  const handleCheckInSuccess = () => {
    refreshCheckIns();
  };

  const handleManualCheckIn = async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter a booking ID, name, email, or phone');
      return;
    }

    setCheckingIn(true);
    try {
      const searchResponse = await apiV2.get(`/organizations/${orgId}/bookings`, {
        params: { query: searchQuery }
      });
      
      const bookings = searchResponse.data.bookings || [];
      
      if (bookings.length === 0) {
        toast.error('No booking found with that search');
        return;
      }
      
      setSearchResults(bookings);
      setSearchResultsPage(1);
      setShowSearchResultsModal(true);
      
      if (bookings.length === 1) {
        toast.success('Found 1 booking!');
      } else {
        toast.success(`Found ${bookings.length} bookings!`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Search failed');
    } finally {
      setCheckingIn(false);
    }
  };

  const checkInBooking = async (bookingId) => {
    const booking = searchResults.find(b => b.id === bookingId);
    if (!booking) {
      toast.error('Booking not found');
      return;
    }
    
    try {
      const response = await apiV2.post(`/organizations/${orgId}/bookings/${booking.id}/check-in`);
      
      if (response.data.alreadyCheckedIn) {
        toast.info('Customer already checked in');
      } else {
        toast.success('✅ Customer checked in successfully!');
      }
      
      setSearchResults(prevResults => 
        prevResults.map(b => 
          b.id === booking.id 
            ? { ...b, checkInStatus: 'checked_in', checkInAt: response.data.checkInAt }
            : b
        )
      );
      
      handleCheckInSuccess();
    } catch (error) {
      console.error('Check-in error:', error);
      toast.error(error.response?.data?.detail || 'Check-in failed');
    }
  };
  
  const openEmailModal = (booking) => {
    setEmailBooking(booking);
    setEmailTo(booking.customerEmail || '');
    setShowEmailModal(true);
  };
  
  // Generate a magic link for a booking
  const generateMagicLink = async (booking) => {
    try {
      const response = await api.post(`/public/bookings/${booking.confirmationCode}/generate-magic-link`);
      const token = response.data.magicToken;
      return `${window.location.origin}/customer-ticket/${booking.confirmationCode}?token=${token}`;
    } catch (error) {
      console.error('Error generating magic link:', error);
      // Fallback to non-magic link if generation fails
      return `${window.location.origin}/customer-ticket/${booking.confirmationCode}`;
    }
  };
  
  const sendTicketEmail = async () => {
    if (!emailTo || !emailTo.trim()) {
      toast.error('Please enter customer email address');
      return;
    }
    
    setSendingEmail(true);
    try {
      // Generate magic link for the email
      const ticketUrl = await generateMagicLink(emailBooking);
      const orgName = localStorage.getItem('organizationName') || 'BookingFlows';
      
      const subject = `Your Booking Ticket - ${orgName}`;
      const message = `Hi ${emailBooking.customerName || 'there'},\n\nHere's your secure booking ticket link:\n\n${ticketUrl}\n\nThis link is personalized for you and will give you direct access to your booking details.\n\nSee you soon!\n\n${orgName}`;
      
      await api.post('/emails/send-to-customer', {
        email: emailTo,
        subject: subject,
        message: message
      });
      
      toast.success('✅ Magic link email sent successfully!');
      setShowEmailModal(false);
      setEmailBooking(null);
      setEmailTo('');
    } catch (error) {
      console.error('Email error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to send email';
      if (errorMsg.includes('rate limit') || errorMsg.includes('too many')) {
        toast.error('Rate limit reached: Maximum 10 emails per customer per day');
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setSendingEmail(false);
    }
  };
  
  const confirmCheckIn = async () => {
    try {
      const response = await apiV2.post(`/organizations/${orgId}/bookings/${bookingToCheckIn.id}/check-in`);
      
      if (response.data.alreadyCheckedIn) {
        toast.info('Customer already checked in');
      } else {
        toast.success('Customer checked in successfully!');
      }
      
      setSearchResults(prevResults => 
        prevResults.map(booking => 
          booking.id === bookingToCheckIn.id 
            ? { ...booking, checkInStatus: 'checked_in', checkInAt: response.data.checkInAt }
            : booking
        )
      );
      
      setShowCheckInConfirm(false);
      setBookingToCheckIn(null);
      handleCheckInSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Check-in failed');
    }
  };

  const formatDateTime = (dateStr, timeStr) => {
    const date = new Date(dateStr + 'T' + timeStr);
    return date.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Filter and sort check-ins (with defensive array check)
  const checkInsArray = Array.isArray(recentCheckIns) ? recentCheckIns : [];
  const filteredCheckIns = checkInsArray.filter(booking => {
    if (filterDate === 'today') {
      const today = new Date().toISOString().split('T')[0];
      return booking.checkInAt && booking.checkInAt.startsWith(today);
    }
    if (filterDate === 'week') {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return booking.checkInAt && new Date(booking.checkInAt) >= weekAgo;
    }
    return true;
  }).sort((a, b) => {
    if (sortBy === 'name') {
      return (a.customerName || '').localeCompare(b.customerName || '');
    }
    if (sortBy === 'service') {
      return (a.serviceName || '').localeCompare(b.serviceName || '');
    }
    return new Date(b.checkInAt || 0) - new Date(a.checkInAt || 0);
  });
  
  // Pagination
  const totalCheckInPages = Math.ceil(filteredCheckIns.length / checkInsPerPage);
  const paginatedCheckIns = filteredCheckIns.slice(
    (checkInPage - 1) * checkInsPerPage,
    checkInPage * checkInsPerPage
  );

  return (
    <TabPageLayout
      title="Check-In System"
      subtitle="QR code scanning and customer check-in management"
      loading={loading}
      actions={
        <div className="flex items-center gap-3">
          {hasUnsavedChanges && (
            <span className="text-xs sm:text-sm text-amber-600 font-medium animate-pulse">
              ⚠️ Unsaved changes
            </span>
          )}
          <ActionButton
            onClick={handleSave}
            loading={saving}
            disabled={checkInEnabled && !/^\d{6}$/.test(checkInPin)}
            icon={CheckCircle}
            className={hasUnsavedChanges ? 'ring-1 ring-amber-400 ring-offset-1' : ''}
          >
            {hasUnsavedChanges ? 'Save Changes' : 'Save Settings'}
          </ActionButton>
        </div>
      }
    >
      <div className="space-y-6">
        {/* STEP 1: Enable/Disable */}
        <FormSection
          stepNumber={1}
          title="Enable Check-In System"
          description="Toggle QR code scanning for customer tickets"
          color="purple"
        >
          <div className="space-y-4">
            {/* Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div>
                <Label className="text-sm sm:text-base font-semibold">Enable Check-In System</Label>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  Show QR code on customer tickets for staff scanning
                </p>
              </div>
              <ServiceToggle
                checked={checkInEnabled}
                onChange={setCheckInEnabled}
              />
            </div>

            {/* PIN Configuration */}
            {checkInEnabled && (
              <div>
                <Label className="text-sm sm:text-base font-semibold mb-2 block">
                  Staff Check-In PIN <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={checkInPin}
                  onChange={(e) => setCheckInPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  className="max-w-xs font-mono text-base text-center h-11"
                />
                <p className="text-xs sm:text-sm text-gray-500 mt-2">
                  6-digit PIN for secure scanner access (tokens expire after 8 hours)
                </p>
              </div>
            )}

            {/* Info Box */}
            {checkInEnabled ? (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <p className="text-sm sm:text-base text-purple-900 font-semibold mb-2">Check-In Active</p>
                <p className="text-xs sm:text-sm text-purple-800">
                  QR codes will appear on all customer tickets. Staff can scan to check in customers.
                </p>
              </div>
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-xs sm:text-sm text-gray-700">
                  Check-in disabled. Great for services that do not need attendance tracking (barbershops, consultations).
                </p>
              </div>
            )}
          </div>
        </FormSection>

        {/* STEP 2: Check In Methods */}
        {checkInEnabled && (
          <FormSection
            stepNumber={2}
            title="Check In Customer"
            description="Choose a method to check in customers"
            color="blue"
          >
            {/* Mode Selector - 3 OPTIONS */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {/* Option 1: QR Scanner Gun - with dropdown */}
              <div className={`p-6 rounded-xl border border-purple-200 bg-gradient-to-br from-purple-50 to-purple-100 ${hasUnsavedChanges ? 'opacity-60' : ''}`}>
                <div className="flex justify-center mb-3">
                  <div className="p-3 bg-purple-600 rounded-full">
                    <QrCode className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="font-bold text-base mb-2 text-center text-gray-900">QR Scanner Gun</div>
                <p className="text-sm text-gray-600 mb-4 text-center">Secure 8-hour session token</p>
                
                {/* Unsaved changes warning */}
                {hasUnsavedChanges && (
                  <div className="mb-3 p-2 bg-amber-100 border border-amber-200 rounded-lg text-center">
                    <p className="text-xs text-amber-700 font-medium">⚠️ Save settings first</p>
                  </div>
                )}
                
                {/* Dropdown Button */}
                <div className="relative" ref={scannerDropdownRef}>
                  <Button
                    onClick={() => {
                      if (hasUnsavedChanges) {
                        toast.error('Please save settings first before opening the scanner');
                        return;
                      }
                      setShowScannerDropdown(!showScannerDropdown);
                    }}
                    className={`w-full flex items-center justify-center gap-2 ${
                      hasUnsavedChanges 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-purple-600 hover:bg-purple-700'
                    } text-white`}
                    disabled={hasUnsavedChanges}
                  >
                    <ExternalLink className="w-4 h-4" />
                    Open Scanner
                    <ChevronDown className={`w-4 h-4 transition-transform ${showScannerDropdown ? 'rotate-180' : ''}`} />
                  </Button>
                  
                  {/* Dropdown Menu */}
                  {showScannerDropdown && !hasUnsavedChanges && (
                    <div className="absolute z-20 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                      <button
                        onClick={async () => {
                          try {
                            const response = await apiV2.post('/check-in/create-scanner-session');
                            const { token, expiresAt } = response.data;
                            
                            const scannerUrl = `${window.location.origin}/qr-code-scanner?token=${token}`;
                            // Navigate in same window for PWA/app compatibility
                            window.location.href = scannerUrl;
                          } catch (error) {
                            console.error('Error creating scanner session:', error);
                            toast.error('Failed to open scanner');
                          }
                        }}
                        className="w-full px-4 py-3 text-left text-sm hover:bg-purple-50 flex items-center gap-3 border-b border-gray-100"
                      >
                        <ExternalLink className="w-4 h-4 text-purple-600" />
                        <div>
                          <p className="font-medium text-gray-900">Open Scanner</p>
                          <p className="text-xs text-gray-500">Launch scanner interface</p>
                        </div>
                      </button>
                      
                      <button
                        onClick={async () => {
                          try {
                            const response = await apiV2.post('/check-in/create-scanner-session');
                            const { token, expiresAt } = response.data;
                            
                            const scannerUrl = `${window.location.origin}/qr-code-scanner?token=${token}`;
                            await navigator.clipboard.writeText(scannerUrl);
                            
                            const expiry = new Date(expiresAt).toLocaleTimeString();
                            toast.success(`Scanner link copied! Share with staff (expires at ${expiry})`);
                            setShowScannerDropdown(false);
                          } catch (error) {
                            console.error('Error creating scanner link:', error);
                            toast.error('Failed to create scanner link');
                          }
                        }}
                        className="w-full px-4 py-3 text-left text-sm hover:bg-purple-50 flex items-center gap-3 border-b border-gray-100"
                      >
                        <Clipboard className="w-4 h-4 text-purple-600" />
                        <div>
                          <p className="font-medium text-gray-900">Copy Link</p>
                          <p className="text-xs text-gray-500">Share scanner URL with staff</p>
                        </div>
                      </button>
                      
                      <button
                        onClick={async () => {
                          try {
                            const response = await apiV2.post('/check-in/create-scanner-session');
                            const { token, expiresAt } = response.data;
                            
                            const scannerUrl = `${window.location.origin}/qr-code-scanner?token=${token}`;
                            setScannerQRUrl(scannerUrl);
                            setShowScannerQR(true);
                            setShowScannerDropdown(false);
                            
                            toast.success('QR Code ready! Staff can scan to open scanner on their phone');
                          } catch (error) {
                            console.error('Error creating scanner QR:', error);
                            toast.error('Failed to generate QR code');
                          }
                        }}
                        className="w-full px-4 py-3 text-left text-sm hover:bg-purple-50 flex items-center gap-3"
                      >
                        <QrCode className="w-4 h-4 text-purple-600" />
                        <div>
                          <p className="font-medium text-gray-900">Show QR Code</p>
                          <p className="text-xs text-gray-500">Staff scan with phone camera</p>
                        </div>
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Option 2: Scan with Phone */}
              <div className="p-6 rounded-xl border border-green-200 bg-gradient-to-br from-green-50 to-green-100 flex flex-col items-center justify-center min-h-[200px]">
                <div className="flex justify-center mb-3">
                  <div className="p-3 bg-green-600 rounded-full">
                    <Camera className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="font-bold text-base mb-2 text-center text-gray-900">Scan with Phone</div>
                <p className="text-sm text-gray-600 text-center">Staff scans QR code on customer ticket, then enters PIN</p>
              </div>
              
              {/* Option 3: Manual Lookup */}
              <button
                onClick={() => setScanMode('manual')}
                className={`p-6 rounded-xl border transition-all text-left ${
                  scanMode === 'manual'
                    ? 'border-blue-500 bg-blue-50 shadow-md'
                    : 'border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100 hover:border-blue-400'
                }`}
              >
                <div className="flex justify-center mb-3">
                  <div className={`p-3 rounded-full ${scanMode === 'manual' ? 'bg-blue-600' : 'bg-blue-500'}`}>
                    <Search className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="font-bold text-base mb-2 text-center text-gray-900">Manual Lookup</div>
                <p className="text-sm text-gray-600 text-center">Search by ID or name</p>
              </button>
            </div>

            {/* Manual Lookup Mode */}
            {scanMode === 'manual' && (
              <div className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search booking ID, name, email, or phone..."
                    className="flex-1 h-12 text-base"
                    onKeyPress={(e) => e.key === 'Enter' && handleManualCheckIn()}
                  />
                  <Button
                    onClick={handleManualCheckIn}
                    disabled={checkingIn || !searchQuery.trim()}
                    className="h-12 px-6 bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {checkingIn ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Search className="w-5 h-5 mr-2" />
                        Search
                      </>
                    )}
                  </Button>
                </div>
                
                {/* Self Check-In Portal Link */}
                <p className="text-sm text-gray-600">
                  💡 Tip: You can also direct customers to check themselves in online if you prefer a{' '}
                  <a 
                    href="/self-check-in" 
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    self-service check-in
                  </a>{' '}process.
                </p>
              </div>
            )}
          </FormSection>
        )}

        {/* STEP 3: Recent Check-Ins */}
        {checkInEnabled && (
          <FormSection
            stepNumber={3}
            title="Recent Check-Ins"
            description={`Showing ${checkInsArray.length} check-ins`}
            color="green"
          >
            <div className="mb-4">
              <Button
                onClick={() => refreshCheckIns()}
                size="sm"
                variant="outline"
                className="flex items-center gap-2"
                disabled={loadingCheckIns}
              >
                <Loader2 className={`w-4 h-4 ${loadingCheckIns ? 'animate-spin' : ''}`} />
                {loadingCheckIns ? 'Refreshing...' : 'Refresh'}
              </Button>
            </div>

            {/* Error State */}
            {checkInsError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-red-900">Failed to load check-ins</p>
                  <p className="text-sm text-red-700 mt-1">Please try refreshing or check your connection.</p>
                </div>
              </div>
            )}
            
            {checkInsArray.length === 0 && !checkInsError ? (
              <EmptyState
                icon={<QrCode />}
                title="No check-ins yet"
                description="Scan your first customer to see them here"
              />
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 max-h-96 overflow-y-auto">
                <div className="space-y-2">
                  {checkInsArray.slice(0, 20).map((booking) => (
                    <div
                      key={booking.id}
                      className="p-3 rounded-lg bg-green-50 border border-green-200"
                    >
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">
                            {booking.customerName}
                          </p>
                          <p className="text-xs text-gray-600 truncate">{booking.serviceName}</p>
                          <p className="text-xs text-gray-500 font-mono">{booking.confirmationCode}</p>
                        </div>
                        <span className="text-xs text-gray-500 flex-shrink-0">
                          {booking.checkInAt ? new Date(booking.checkInAt).toLocaleTimeString() : '-'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </FormSection>
        )}
      </div>

      {/* Check-In Details Modal */}
      <Dialog open={showCheckInDetails} onOpenChange={setShowCheckInDetails}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Check-In Details</DialogTitle>
          </DialogHeader>
          
          {selectedCheckIn && (
            <div className="space-y-4">
              {/* Customer Info */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Customer</p>
                <p className="font-bold text-lg text-gray-900">{selectedCheckIn.customerName}</p>
                {selectedCheckIn.customerPhone && (
                  <p className="text-sm text-gray-600 mt-1">📞 {selectedCheckIn.customerPhone}</p>
                )}
              </div>

              {/* Booking Info */}
              {(selectedCheckIn.preferredDate || selectedCheckIn.preferredTime) && (
                <div className="grid grid-cols-2 gap-4">
                  {selectedCheckIn.preferredDate && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Date</p>
                      <p className="font-semibold text-gray-900">{selectedCheckIn.preferredDate}</p>
                    </div>
                  )}
                  {selectedCheckIn.preferredTime && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Time</p>
                      <p className="font-semibold text-gray-900">{selectedCheckIn.preferredTime}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Service */}
              {selectedCheckIn.serviceName && (
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Service</p>
                  <p className="font-semibold text-gray-900">{selectedCheckIn.serviceName}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                {selectedCheckIn.confirmationCode && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Booking ID</p>
                    <p className="font-mono font-bold text-blue-600">{selectedCheckIn.confirmationCode}</p>
                  </div>
                )}
                {selectedCheckIn.partySize && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Party Size</p>
                    <p className="font-semibold text-gray-900">{selectedCheckIn.partySize} guests</p>
                  </div>
                )}
              </div>

              {/* Check-In Info */}
              {selectedCheckIn.checkInStatus === 'checked_in' ? (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <p className="text-xs text-purple-700 uppercase tracking-wide mb-2">Check-In Status</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-purple-600" />
                      <span className="font-semibold text-purple-900">Checked In</span>
                    </div>
                    {selectedCheckIn.checkInAt && (
                      <div className="text-right">
                        <p className="text-sm font-medium text-purple-800">
                          {new Date(selectedCheckIn.checkInAt).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit'
                          })}
                        </p>
                        <p className="text-xs text-purple-600 capitalize">
                          via {selectedCheckIn.checkInMethod || 'manual'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-xs text-amber-700 uppercase tracking-wide mb-2">Check-In Status</p>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-amber-600" />
                    <span className="font-semibold text-amber-900">Not Checked In</span>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={async () => {
                    try {
                      const ticketUrl = await generateMagicLink(selectedCheckIn);
                      await navigator.clipboard.writeText(ticketUrl);
                      toast.success('Magic link copied!');
                    } catch (error) {
                      toast.error('Failed to generate magic link');
                    }
                  }}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Copy Magic Link
                </Button>
                <Button
                  onClick={() => setShowCheckInDetails(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
        
      {/* Check-In Confirmation Modal */}
      <Dialog open={showCheckInConfirm} onOpenChange={setShowCheckInConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Check-In</DialogTitle>
          </DialogHeader>
          
          {bookingToCheckIn && (
            <div className="space-y-4">
              <p className="text-lg font-semibold text-gray-900">
                {bookingToCheckIn.customerName}
              </p>
              <p className="text-gray-600">
                {bookingToCheckIn.serviceName || 'Service'}
              </p>
              {(bookingToCheckIn.date || bookingToCheckIn.preferredDate) && (
                <p className="text-sm text-gray-600">
                  {bookingToCheckIn.date || bookingToCheckIn.preferredDate} at {bookingToCheckIn.startTime || bookingToCheckIn.preferredTime}
                </p>
              )}
              {bookingToCheckIn.groupSize && (
                <p className="text-sm font-medium text-gray-700">
                  Party size: {bookingToCheckIn.groupSize} guests
                </p>
              )}
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="font-medium text-amber-900 mb-2">Before checking in:</p>
                <p className="text-sm text-amber-800">Is the customer present for this booking?</p>
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  onClick={() => setShowCheckInConfirm(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmCheckIn}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  Yes, Check In
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    
      {/* Search Results Modal - Table Design */}
      <Dialog open={showSearchResultsModal} onOpenChange={setShowSearchResultsModal}>
        <DialogContent className="max-w-[95vw] max-h-[90vh] overflow-hidden flex flex-col p-0 rounded-xl">
          {/* Header */}
          <div className="px-5 pt-5 pb-4 border-b border-gray-100">
            <DialogHeader>
              <DialogTitle className="text-lg sm:text-xl font-bold text-gray-900">Booking Lookup</DialogTitle>
            </DialogHeader>
            
            {/* Search Bar */}
            <div className="mt-4 flex gap-2 items-center">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Name, email, phone, or booking ID..."
                  className="pl-10 h-10 sm:h-11 bg-gray-50 border-gray-200 focus:bg-white text-sm"
                  onKeyPress={(e) => e.key === 'Enter' && handleManualCheckIn()}
                />
              </div>
              <Button
                onClick={handleManualCheckIn}
                disabled={checkingIn || !searchQuery.trim()}
                className="h-10 sm:h-11 px-4 bg-blue-600 hover:bg-blue-700 text-white"
              >
                {checkingIn ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-2" />Search</>}
              </Button>
            </div>
            
            {/* Results count */}
            <p className="text-xs sm:text-sm text-gray-500 mt-3">
              Found <span className="font-semibold text-gray-700">{searchResults.length}</span> booking(s)
              {searchResults.length > searchResultsPerPage && (
                <span className="text-gray-400"> · Page {searchResultsPage} of {Math.ceil(searchResults.length / searchResultsPerPage)}</span>
              )}
            </p>
          </div>
          
          {/* Table */}
          <div className="flex-1 overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">Status</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">Booking ID</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">Customer</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">Service</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">Date & Time</th>
                  <th className="px-3 py-3 text-center font-semibold text-gray-600 whitespace-nowrap">Party</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">Details</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">Payment</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">Ticket</th>
                  <th className="px-3 py-3 text-center font-semibold text-gray-600 whitespace-nowrap">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {searchResults
                  .slice(
                    (searchResultsPage - 1) * searchResultsPerPage,
                    searchResultsPage * searchResultsPerPage
                  )
                  .map((booking) => {
                    // Calculate payment status
                    const totalAmount = booking.totalAmount || 0;
                    const amountPaid = booking.paidAmount || booking.amountPaid || 0;
                    const amountOwed = Math.max(0, totalAmount - amountPaid);
                    const isPaidInFull = (amountOwed === 0 && totalAmount > 0) || booking.paymentStatus === 'paid';
                    const isPartiallyPaid = amountPaid > 0 && amountOwed > 0;
                    const isUnpaid = amountPaid === 0 && totalAmount > 0;
                    
                    // Parse add-ons and custom question answers
                    const addOns = booking.selectedAddOns || booking.addOns || [];
                    const customAnswers = booking.customQuestionAnswers || booking.customAnswers || [];
                    const hasExtras = addOns.length > 0 || customAnswers.length > 0;
                    
                    // Format time with duration and timezone
                    const startTime = booking.startTime || booking.preferredTime || '-';
                    const endTime = booking.endTime || '';
                    const timezone = booking.timezone || '';
                    const duration = booking.duration;
                    
                    return (
                      <tr 
                        key={booking.id} 
                        className={`hover:bg-gray-50 transition-colors ${
                          booking.checkInStatus === 'checked_in' ? 'bg-green-50/50' : ''
                        }`}
                      >
                        {/* Status */}
                        <td className="px-3 py-3">
                          {booking.checkInStatus === 'checked_in' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-green-100 text-green-700 whitespace-nowrap">
                              <Check className="w-3 h-3" />
                              Checked In
                            </span>
                          ) : (
                            <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-600 whitespace-nowrap">
                              Not Checked In
                            </span>
                          )}
                        </td>
                        
                        {/* Booking ID */}
                        <td className="px-3 py-3">
                          <span className="font-mono text-xs font-semibold text-gray-900">{booking.confirmationCode || '-'}</span>
                        </td>
                        
                        {/* Customer */}
                        <td className="px-3 py-3">
                          <div>
                            <p className="font-semibold text-gray-900 whitespace-nowrap">{booking.customerName || '-'}</p>
                            <p className="text-xs text-gray-500">{booking.customerEmail || ''}</p>
                            {booking.customerPhone && <p className="text-xs text-gray-500">{booking.customerPhone}</p>}
                          </div>
                        </td>
                        
                        {/* Service */}
                        <td className="px-3 py-3">
                          <span className="text-sm max-w-[120px] truncate block" title={booking.serviceName}>{booking.serviceName || '-'}</span>
                        </td>
                        
                        {/* Date & Time (combined with duration & timezone) */}
                        <td className="px-3 py-3">
                          <div className="text-xs">
                            <p className="font-medium text-gray-900 whitespace-nowrap">
                              {(booking.date || booking.preferredDate) 
                                ? new Date(booking.date || booking.preferredDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                : '-'}
                            </p>
                            <p className="text-gray-700 whitespace-nowrap">
                              {startTime}{endTime ? ` - ${endTime}` : ''}{duration ? ` (${duration}min)` : ''}
                            </p>
                            {timezone && <p className="text-gray-400 text-[10px]">{timezone}</p>}
                          </div>
                        </td>
                        
                        {/* Party Size */}
                        <td className="px-3 py-3 text-center">
                          <span className="text-sm font-medium text-gray-700">{booking.groupSize || booking.partySize || '-'}</span>
                        </td>
                        
                        {/* Details: Add-ons + Custom Answers */}
                        <td className="px-3 py-3">
                          <div className="text-xs space-y-1 max-w-[200px]">
                            {/* Add-ons */}
                            {addOns.length > 0 && (
                              <div>
                                <p className="text-gray-500 font-medium">Add-ons:</p>
                                {addOns.slice(0, 2).map((addon, idx) => (
                                  <p key={idx} className="text-gray-700 truncate">
                                    • {addon.title || addon.name || 'Add-on'}{addon.quantity > 1 ? ` (x${addon.quantity})` : ''}{addon.price ? ` - $${addon.price}` : ''}
                                  </p>
                                ))}
                                {addOns.length > 2 && <p className="text-gray-400">+{addOns.length - 2} more</p>}
                              </div>
                            )}
                            
                            {/* Custom Question Answers */}
                            {customAnswers.length > 0 && (
                              <div>
                                <p className="text-gray-500 font-medium">Custom Info:</p>
                                {customAnswers.slice(0, 2).map((qa, idx) => (
                                  <p key={idx} className="text-gray-700 truncate" title={`${qa.question}: ${qa.answer}`}>
                                    • {qa.answer || qa.value || '-'}
                                  </p>
                                ))}
                                {customAnswers.length > 2 && <p className="text-gray-400">+{customAnswers.length - 2} more</p>}
                              </div>
                            )}
                            
                            {/* Check-in time if checked in */}
                            {booking.checkInStatus === 'checked_in' && booking.checkInAt && (
                              <p className="text-green-600">
                                ✓ {new Date(booking.checkInAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </p>
                            )}
                            
                            {!hasExtras && !booking.checkInAt && <span className="text-gray-400">-</span>}
                          </div>
                        </td>
                        
                        {/* Payment */}
                        <td className="px-3 py-3">
                          <div className="space-y-1">
                            {isPaidInFull ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-green-100 text-green-700 whitespace-nowrap">
                                <Check className="w-3 h-3" />
                                Paid
                              </span>
                            ) : isPartiallyPaid ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-amber-100 text-amber-700 whitespace-nowrap">
                                <AlertTriangle className="w-3 h-3" />
                                Partial
                              </span>
                            ) : isUnpaid ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-red-100 text-red-700 whitespace-nowrap">
                                Unpaid
                              </span>
                            ) : (
                              <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-600">
                                {booking.paymentStatus || 'N/A'}
                              </span>
                            )}
                            <p className="text-xs font-semibold text-gray-900">
                              {totalAmount > 0 ? `$${totalAmount.toFixed(2)}` : '-'}
                            </p>
                            {booking.addOnsTotal > 0 && (
                              <p className="text-[10px] text-gray-500">+${booking.addOnsTotal.toFixed(2)} add-ons</p>
                            )}
                          </div>
                        </td>
                        
                        {/* Ticket Actions */}
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={async () => {
                                try {
                                  const ticketUrl = await generateMagicLink(booking);
                                  await navigator.clipboard.writeText(ticketUrl);
                                  toast.success('Magic link copied!');
                                } catch (error) {
                                  toast.error('Failed to generate magic link');
                                }
                              }}
                              className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                              title="Copy magic link"
                            >
                              <Clipboard className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => openEmailModal(booking)}
                              className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                              title="Email magic link"
                            >
                              <Mail className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                        
                        {/* Action */}
                        <td className="px-3 py-3 text-center">
                          {booking.checkInStatus !== 'checked_in' ? (
                            <Button
                              onClick={() => checkInBooking(booking.id)}
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white h-8 px-3 whitespace-nowrap"
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Check In
                            </Button>
                          ) : (
                            <span className="text-green-600 font-medium text-sm whitespace-nowrap">✓ Done</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
            
            {/* Empty State */}
            {searchResults.length === 0 && (
              <div className="text-center py-12">
                <Search className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">No bookings found</p>
                <p className="text-gray-400 text-xs mt-1">Try a different search term</p>
              </div>
            )}
          </div>
          
          {/* Footer with pagination */}
          <div className="px-5 py-4 border-t border-gray-100 bg-gray-50/50">
            {/* Pagination */}
            {searchResults.length > searchResultsPerPage && (
              <div className="flex items-center justify-center gap-2 mb-4">
                <Button
                  onClick={() => setSearchResultsPage(p => Math.max(1, p - 1))}
                  disabled={searchResultsPage === 1}
                  variant="outline"
                  size="sm"
                  className="h-8"
                >
                  Previous
                </Button>
                
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.ceil(searchResults.length / searchResultsPerPage) }, (_, i) => i + 1).map(pageNum => (
                    <button
                      key={pageNum}
                      onClick={() => setSearchResultsPage(pageNum)}
                      className={`w-8 h-8 rounded-lg text-sm font-medium transition ${
                        searchResultsPage === pageNum
                          ? 'bg-blue-600 text-white'
                          : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  ))}
                </div>
                
                <Button
                  onClick={() => setSearchResultsPage(p => Math.min(Math.ceil(searchResults.length / searchResultsPerPage), p + 1))}
                  disabled={searchResultsPage === Math.ceil(searchResults.length / searchResultsPerPage)}
                  variant="outline"
                  size="sm"
                  className="h-8"
                >
                  Next
                </Button>
              </div>
            )}
            
            {/* Action Buttons */}
            <div className="flex gap-2">
              <Button
                onClick={() => setShowSearchResultsModal(false)}
                variant="outline"
                className="flex-1 h-10"
              >
                Close
              </Button>
              <Button
                onClick={() => {
                  setSearchResults([]);
                  setSearchQuery('');
                  setSearchResultsPage(1);
                  setShowSearchResultsModal(false);
                }}
                className="flex-1 h-10 bg-blue-600 hover:bg-blue-700 text-white"
              >
                Clear & Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Send Ticket via Email</DialogTitle>
          </DialogHeader>
          
          {emailBooking && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-blue-900 mb-1">
                  {emailBooking.customerName || 'Customer'}
                </p>
                <p className="text-xs text-blue-700">
                  Booking: {emailBooking.confirmationCode}
                </p>
              </div>
              
              <div>
                <Label className="block text-sm font-semibold text-gray-700 mb-2">
                  Customer Email <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="email"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  placeholder="Enter customer email..."
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {emailBooking.customerEmail ? 'Pre-filled from booking' : 'No email on file - please ask customer for their email'}
                </p>
              </div>
              
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-xs font-semibold text-gray-700 mb-2">Email Preview:</p>
                <p className="text-xs text-gray-600 whitespace-pre-line">
                  Hi {emailBooking.customerName || 'there'}!
                  {'\n\n'}
                  Here is your booking ticket:
                  {'\n'}
                  [Ticket Link]
                  {'\n\n'}
                  See you soon!
                  {'\n\n'}
                  {localStorage.getItem('organizationName') || 'BookingFlows'}
                </p>
                <p className="text-xs text-gray-500 mt-2 italic">
                  Fixed template - ensures professional communication
                </p>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs text-amber-800">
                  <strong>Rate Limit:</strong> Maximum 10 emails per customer per day
                </p>
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={() => {
                    setShowEmailModal(false);
                    setEmailTo('');
                  }}
                  variant="outline"
                  className="flex-1"
                  disabled={sendingEmail}
                >
                  Cancel
                </Button>
                <Button
                  onClick={sendTicketEmail}
                  disabled={sendingEmail || !emailTo.trim()}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {sendingEmail ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Send Email
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Scanner QR Code Modal */}
      <Dialog open={showScannerQR} onOpenChange={setShowScannerQR}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg">
                <QrCode className="w-5 h-5 text-white" />
              </div>
              Scan to Open Scanner
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Staff can scan this QR code with their phone camera to instantly open the scanner interface
            </p>
            
            {/* QR Code */}
            <div className="bg-white border-4 border-gray-100 rounded-xl p-6 flex items-center justify-center">
              {scannerQRUrl && (
                <QRCodeCanvas
                  value={scannerQRUrl}
                  size={280}
                  level="H"
                  includeMargin={true}
                />
              )}
            </div>
            
            {/* Info */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <p className="text-xs text-purple-900 font-medium mb-1">⏱️ Session Info</p>
              <p className="text-xs text-purple-800">
                Scanner session valid for 8 hours. Staff can scan this QR code to check in customers on their phone.
              </p>
            </div>
            
            {/* URL Preview */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-gray-500">Scanner URL:</p>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(scannerQRUrl);
                    toast.success('URL copied!');
                  }}
                  className="text-xs text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
                >
                  <Clipboard className="w-3.5 h-3.5" />
                  Copy
                </button>
              </div>
              <div className="bg-gray-100 rounded-lg p-2 font-mono text-xs text-gray-700 break-all">
                {scannerQRUrl}
              </div>
            </div>
            
            {/* Buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                onClick={() => setShowScannerQR(false)}
                variant="outline"
                className="flex-1"
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </TabPageLayout>
  );
};

export default CheckInTab;
